import { Injectable } from '@angular/core';
import { Photographer } from '../models/new/photographer';
import { ColorShots, Order, OrderDetail, OrderFilters, OrderStatus } from '../models/new/order';
import { Status } from '../models/new/status';
import { Collection } from '../models/new/collection';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { DateRange } from '../models/new/dateRange';
import { AspectRatio, CodifiedUse, MediaFormat, Shot, ShotType } from '../models/new/shot';
import { Category } from '../models/new/category';
import { Brand } from '../models/new/brand.model';
import { Color, Gender, SizeGroup, Style } from '../models/new/style';
import { Template, TemplateFilters } from '../models/new/template';
import * as _ from 'lodash';
import { StyleGroup } from '../models/new/style-group';
import { Team } from '../models/new/team';

@Injectable({
	providedIn: 'root'
})
export class TemplateMapper {
	constructor(private utils: UtilitiesService) {}

	toMediaFormat(mediaFormat: any) {
		return new MediaFormat({
			id: mediaFormat.id,
			name: mediaFormat.description,
			code: mediaFormat.code
		});
	}

	toAspectRatio(aspectRatio: any) {
		return new AspectRatio({
			id: aspectRatio.id,
			label: aspectRatio.code,
			orientation: aspectRatio.code == '1-1' ? 'Square' : aspectRatio.code.endsWith('-9') ? 'Portrait' : 'Landscape'
		});
	}

	toCodifiedUse(codifiedUse: any) {
		return new CodifiedUse({
			id: codifiedUse.id,
			name: codifiedUse.description,
			code: codifiedUse.code
		});
	}

	toShotType(shotType: any) {
		return new ShotType({
			id: shotType.id,
			name: shotType.name,
			code: shotType.code
		});
	}

	toTemplate(template: any) {
		console.log('toTemplate', template);
		return new Template({
			id: template.templateId,
			name: template.templateName,
			brand: template.brand ? new Brand({ id: template.brand.id, name: template.brand.label }) : undefined,
			category: template.category ? new Category({ id: template.category.id, name: template.category.label }) : undefined,
			isDefault: template.isDefault,
			styles: template.styles?.map((s: any) => {
				return new Style({
					modelId: s.modelId,
					name: s.styleName,
					code: s.styleCode
				});
			}),
			colors: template.colors?.map((c: any) => new Color({ id: c.id, name: c.finalColorName, code: c.code })),
			collection: template.collection ? new Collection({ id: template.collection.id, name: template.collection.label }) : undefined,
			gender: template.gender ? new Gender({ id: template.gender.id, name: template.gender.label }) : undefined,
			styleGroup: template.styleGroup ? new StyleGroup({ id: template.styleGroup.id, name: template.styleGroup.label }) : undefined,
			shots: template.shotTypes.map(
				(s: any) =>
					new Shot({
						id: s.shotType.id,
						shotType: s.shotType ? new ShotType({ id: s.shotType.id, name: s.shotType.label }) : undefined,
						mediaFormat: s.mediaFormat ? new MediaFormat({ id: s.mediaFormat.id, name: s.mediaFormat.label, code: s.mediaFormat.label }) : undefined,
						codifiedUse: s.codifiedUse ? new CodifiedUse({ id: s.codifiedUse.id, name: s.codifiedUse.label, code: s.codifiedUse.label }) : undefined,
						aspectRatio: s.aspectRatio ? new AspectRatio({ id: s.aspectRatio.id, label: s.aspectRatio.label, orientation: s.aspectRatio.label.trim() == '1-1' ? 'Square' : s.aspectRatio.label.trim().endsWith('-9') ? 'Portrait' : 'Landscape' }) : undefined
					})
			)
		});
	}

	toWhereClause(filters: TemplateFilters) {
		return {
			/*orderId: filters.ids ? { in: filters.ids } : undefined,
			dueDate: filters.dueDate ? { gte: filters.dueDate.from, lte: filters.dueDate.to } : undefined,
			creationDate: filters.creationDate ? { gte: filters.creationDate.from, lte: filters.creationDate.to } : undefined,
			completionDate: filters.completionDate ? { gte: filters.completionDate.from, lte: filters.completionDate.to } : undefined,
			photographerPersonId: filters.photographers?.length ? { in: filters.photographers.map((p) => p.id) } : undefined,
			orderStatusId: filters.statuses?.length ? { in: filters.statuses.map((s) => s.id) } : undefined*/
			brand: filters.brands?.length ? { id: { in: filters.brands.map((b) => b.id) } } : undefined,
			category: filters.categories?.length ? { id: { in: filters.categories.map((c) => c.id) } } : undefined,
			styleGroup: filters.styleGroups?.length ? { id: { in: filters.styleGroups.map((sg) => sg.id) } } : undefined
			//team: filters.teams?.length ? { id: { in: filters.teams.map((t) => t.id) } } : undefined
		};
	}

	toTemplateQL(template: Template) {
		return {
			templateId: template.id || undefined,
			brandId: template.brand?.id || undefined,
			collectionId: template.collection?.id || undefined,
			categoryId: template.category?.id || undefined,
			genderId: template.gender?.id || undefined,
			styleGroupId: template.styleGroup?.id || undefined,
			modelIds: template.styles?.map((s: Style) => s.modelId),
			colorIds: _.flatten(template.styles?.map((c: Style) => c.colors?.map((color: Color) => color.id))),
			shotTypes: template.shots?.map((shot: Shot, index) => {
				return {
					shotTypeId: shot.shotType?.id || undefined,
					aspectRatioId: shot.aspectRatio?.id,
					mediaFormatId: shot.mediaFormat?.id,
					codifiedUseId: shot.codifiedUse?.id,
					ord: index + 1
				};
			})
		};
	}

	filtersToUrl(filters: TemplateFilters): any {
		return {
			brands: filters.brands ? filters.brands.map((brand) => brand.id) : undefined,
			categories: filters.categories ? filters.categories.map((category) => category.id) : undefined,
			styleGroups: filters.styleGroups ? filters.styleGroups.map((styleGroup) => styleGroup.id) : undefined,
			teams: filters.teams ? filters.teams.map((team) => team.id) : undefined,
			page: filters.page || 1,
			size: filters.size || 20
		};
	}

	urlToFilters(url: any) {
		return new TemplateFilters({
			brands: url.brands ? (Array.isArray(url.brands) ? url.brands.map((id: any) => new Brand({ id: Number(id) })) : [new Brand({ id: Number(url.brands) })]) : undefined,
			categories: url.categories ? (Array.isArray(url.categories) ? url.categories.map((id: any) => new Category({ id: Number(id) })) : [new Category({ id: Number(url.categories) })]) : undefined,
			styleGroups: url.styleGroups ? (Array.isArray(url.styleGroups) ? url.styleGroups.map((id: any) => new StyleGroup({ id: Number(id) })) : [new StyleGroup({ id: Number(url.styleGroups) })]) : undefined,
			teams: url.teams ? (Array.isArray(url.teams) ? url.teams.map((id: any) => new Team({ id: Number(id) })) : [new Team({ id: Number(url.teams) })]) : undefined,
			page: Number(url.page) || 1,
			size: Number(url.size) || 20
		});
	}
}
